import React, { useCallback, useEffect, useState } from 'react';
import { compareDatesAsc, compareNumbersAsc } from '@web/utils';
import { createUseStyles } from 'react-jss';
import { dmsActions } from '@packages/contexts/dms';
import { dmsService } from '@web/services/singletons';
import { DmsTableHeader } from './dms-table-header';
import { DmsTableRow } from './dms-table-row';
import { Icon, Typography } from '@packages/ui/shared';
import { Layout } from './layout';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { RepairOrder } from '@packages/models/api';
import { TableRenderer, SORT_DIRECTION } from '@web/components/table';
import { useDmsDispatch, useDmsState } from '@packages/contexts/dms';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

export interface DmsTableRowModel extends RepairOrder {
    checked: boolean;
    rowId: string;
}

const DMS_TITLE = 'Repair Order History';
const EMPTY_STATE_DESC = 'No Repair Orders available for this vehicle yet.';
const DMS_TOOLTIP = 'The data updates daily with closed RO information only';

const useDmsListStyles = createUseStyles({
    tableContainer: {
        minHeight: '82vh',
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
    tooltip: {
        marginTop: 5,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            maxWidth: 272,
            padding: '3px 8px',
            textAlign: 'left',
            width: 'auto',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
        },
    },
});

function getRepairOrderComparator(
    sortBy: string,
    sortDirection: SORT_DIRECTION
): (a: DmsTableRowModel, b: DmsTableRowModel) => number {
    if (sortBy === 'closedDate') {
        return sortDirection === SORT_DIRECTION.ASC
            ? (a, b) => compareDatesAsc(a.closedDate, b.closedDate)
            : (a, b) => compareDatesAsc(b.closedDate, a.closedDate);
    }
    if (sortBy === 'mileage') {
        return sortDirection === SORT_DIRECTION.ASC
            ? (a, b) => compareNumbersAsc(a.mileage, b.mileage)
            : (a, b) => compareNumbersAsc(b.mileage, a.mileage);
    }

    return () => 0;
}

export const DmsList = () => {
    const classes = useDmsListStyles();
    const { vin: vinString } = useParams<{ vin: string }>();
    const { loading, repairOrdersHistory, hasHistoryFetched, sortState } = useDmsState();
    const dispatch = useDmsDispatch();
    const { t } = useTranslation();
    const [roTableRows, setRoTableRows] = useState<DmsTableRowModel[]>([]);
    const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

    useEffect(() => {
        if (vinString && !hasHistoryFetched) {
            dmsService.fetchAndSetRepairOrderHistory(vinString, dispatch);
        }
    }, [vinString, hasHistoryFetched, dispatch]);

    const sortRepairOrderRows = useCallback(
        (rows: DmsTableRowModel[], sortBy: string | null = null, sortDirection: SORT_DIRECTION | null = null) => {
            if (!sortBy || !sortDirection) {
                return rows;
            }
            return [...rows].sort(getRepairOrderComparator(sortBy, sortDirection));
        },
        []
    );

    useEffect(() => {
        if (hasHistoryFetched && repairOrdersHistory.length > 0) {
            const repairOrderRows = repairOrdersHistory.map((ro: RepairOrder) => ({
                ...ro,
                rowId: ro.roNumber ?? Math.random().toString(),
                checked: false,
            }));

            setRoTableRows(
                sortRepairOrderRows(
                    repairOrderRows,
                    sortState.roTable.sortBy,
                    sortState.roTable.sortDirection as SORT_DIRECTION
                )
            );
        } else {
            setRoTableRows([]);
        }
    }, [
        hasHistoryFetched,
        repairOrdersHistory,
        sortState.roTable.sortBy,
        sortState.roTable.sortDirection,
        sortRepairOrderRows,
    ]);

    function handleSort(key: string, direction: SORT_DIRECTION) {
        dispatch(dmsActions.setSort({ sortBy: key, sortDirection: direction }));
        setRoTableRows((prev) => sortRepairOrderRows(prev, key, direction));
    }

    const renderHeader = useCallback(
        () => (
            <DmsTableHeader
                onSort={handleSort}
                sortBy={sortState.roTable.sortBy}
                sortDirection={sortState.roTable.sortDirection as SORT_DIRECTION}
            />
        ),
        [handleSort, sortState.roTable.sortBy, sortState.roTable.sortDirection]
    );

    const renderRow = useCallback(
        (rowData: DmsTableRowModel) => (
            <DmsTableRow
                key={rowData.rowId}
                rowData={rowData}
                expandedRowId={expandedRowId}
                setExpandedRowId={setExpandedRowId}
            />
        ),
        [expandedRowId]
    );

    return (
        <Layout>
            <div className={`pt-7 pl-7 pr-7 d-flex ${!repairOrdersHistory?.length && classes.tableContainer}`}>
                <div className="d-flex w-100 flex-column">
                    <header className="mb-6 d-flex align-items-center">
                        <Typography variant="h2">{t(DMS_TITLE)}</Typography>
                        <OverlayTrigger
                            key={'info'}
                            placement="bottom"
                            overlay={
                                <Tooltip className={classes.tooltip} id={'info-tool-tip'}>
                                    {t(DMS_TOOLTIP)}
                                </Tooltip>
                            }
                        >
                            <Icon name="info" style={{ marginLeft: 15 }} color="black" />
                        </OverlayTrigger>
                    </header>

                    <TableRenderer<DmsTableRowModel>
                        emptyStateProps={{
                            icon: 'car-repair',
                            description: t(EMPTY_STATE_DESC),
                        }}
                        isLoading={loading}
                        useFixedLayout
                        outerTableClassName={classes.outerTable}
                        tableBodyRowRenderer={renderRow}
                        tableHeaderRowRenderer={renderHeader}
                        tableRowsData={roTableRows}
                        tableRowsDataSetter={setRoTableRows}
                    />
                </div>
            </div>
        </Layout>
    );
};
